import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, Grid, OrbitControls, Stars, Text, Image } from '@react-three/drei';
import * as THREE from 'three';
import { ContentSurface } from './ContentSurface';
import { VideoHorizon } from './VideoHorizon';
import { HolographicChat } from './HolographicChat';
import { HolographicVinyl } from './HolographicVinyl';
import { useSovereignSync } from '../hooks/useSovereignSync';

interface HorizonDeckProps {
  envUrl?: string;    // Custom HDRI URL
  videoUrl?: string;  // Custom Video URL
  youtubeId?: string; // Custom YouTube ID
  themeColor?: string;
  preset?: string;
  messages?: any[];   // Chat History
  showGrid?: boolean; // Visibility Toggle
  // Audio Metadata
  albumArtUrl?: string;
  artist?: string;
  trackTitle?: string;
}

export function HorizonDeck({ envUrl, videoUrl, youtubeId, themeColor = '#00f3ff', preset = 'city', messages = [], showGrid = true, albumArtUrl, artist, trackTitle }: HorizonDeckProps) {

  // Real-Time Sync Hook
  const { isConnected, lastMessage, broadcast } = useSovereignSync();
  const [remoteMedia, setRemoteMedia] = useState<{ mode?: string, youtube?: string, sc?: string } | null>(null);

  useEffect(() => {
    if (lastMessage?.payload?.type === 'MEDIA_CHANGE') {
      setRemoteMedia(lastMessage.payload.data);
    }
  }, [lastMessage]);

  // Determine Media Configurations
  const mediaConfig = useMemo(() => {
    // 1. Remote Override (High Priority)
    if (remoteMedia) return remoteMedia;

    // 2. Props (Medium Priority)
    if (youtubeId) return { mode: 'video', youtube: youtubeId };

    if (!videoUrl) return { mode: 'radio', youtube: 'jfKfPfyJRdk' }; // Default Lofi

    if (videoUrl.includes('blob:')) return { mode: 'zen', youtube: undefined }; // Local File

    if (videoUrl.includes('lofi')) return { mode: 'video', youtube: 'jfKfPfyJRdk' }; // Lofi Girl

    if (videoUrl.includes('synth') || videoUrl.includes('matrix')) {
      return { mode: 'radio', youtube: 'M5QY2_8704o', sc: 'https://api.soundcloud.com/playlists/1732126665' }; // Synthwave
    }

    if (videoUrl.includes('hyperspace')) {
      return { mode: 'video', youtube: 'tNkZsRW7h2c' }; // Space Walk Ambience 
    }

    if (videoUrl.includes('ocean')) {
      return { mode: 'zen', youtube: 'bn9F19Hi1Lk' }; // Ocean Sounds
    }

    return { mode: 'radio', youtube: 'jfKfPfyJRdk' };
  }, [videoUrl, youtubeId, remoteMedia]);

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'absolute', top: 0, left: 0, zIndex: 0 }}>
      <Canvas camera={{ position: [0, 2, 5], fov: 60 }} gl={{ toneMapping: THREE.ACESFilmicToneMapping }}>

        {/* 1. THE WORLD */}
        <React.Suspense fallback={null}>
          {videoUrl ? (
            <VideoHorizon url={videoUrl} />
          ) : (
            <Environment files={envUrl} preset={envUrl ? undefined : (preset as any)} background blur={0.06} />
          )}
        </React.Suspense>

        <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />

        {showGrid && (
          <Grid infiniteGrid sectionSize={3} sectionColor={themeColor} sectionThickness={1} cellColor={themeColor} cellThickness={0.5} fadeDistance={40} fadeStrength={1} />
        )}

        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} color={themeColor} />
        <fog attach="fog" args={['#050505', 10, 60]} />

        {/* 4. CONTENT */}
        <HolographicLogo isConnected={isConnected} />

        {/* MEDIA DISPLAY */}
        <ContentSurface mediaMode={mediaConfig.mode as any} youtubeId={mediaConfig.youtube} soundcloudUrl={mediaConfig.sc} />

        {/* AUDIO VISUALIZATION */}
        <HolographicVinyl imageUrl={albumArtUrl} artist={artist} title={trackTitle} />

        <HolographicChat messages={messages} />
        <Rig />

        <OrbitControls autoRotate={false} enableZoom={true} enablePan={true} panSpeed={2} zoomSpeed={2} rotateSpeed={1.5} maxDistance={30} minDistance={1} maxPolarAngle={Math.PI} minPolarAngle={0} />
      </Canvas>
    </div>
  );
}

function Rig() {
  useFrame((state) => {
    // Subtle floating camera movement
    state.camera.position.y = THREE.MathUtils.lerp(state.camera.position.y, 2 + Math.sin(state.clock.elapsedTime / 4) / 4, 0.01)
  })
  return null
}

function HolographicLogo({ isConnected }: { isConnected?: boolean }) {
  return (
    <group position={[0, 2.5, -4]}>
      {/* The Logo Plane */}
      <Image
        url="/logo.png"
        transparent
        scale={[3, 3, 1]}
        toneMapped={false}
      />
      {/* Minimal Subtext */}
      <Text
        position={[0, -1.8, 0]}
        fontSize={0.25}
        color={isConnected ? "#00ff00" : "#00f3ff"}
        anchorX="center"
        anchorY="middle"
        letterSpacing={0.2}
      >
        {isConnected ? "SOVEREIGN LINK: ACTIVE" : "SOVEREIGN INTELLIGENCE"}
      </Text>
    </group>
  )
}
